import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Ruler, Waypoints, Move, ArrowRight } from "lucide-react";
import { VerificationBadge } from "@/components/Badges";
import { priceLabel, inr, fmtDate } from "@/lib/loop";
import { Button } from "@/components/ui/button";

export default function PropertyCard({ p, index = 0, testid }) {
  const nav = useNavigate();
  const facts = [
    { Icon: Ruler, v: p.area_display || `${p.area_sqft} sqft` },
    { Icon: Waypoints, v: p.road_width ? `${p.road_width} road` : "—" },
    { Icon: Move, v: p.frontage && p.frontage !== "—" ? `${p.frontage} front` : "—" },
  ];
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      className="group rounded-2xl bg-[#131B35] border border-white/10 overflow-hidden hover:border-[#C5A059]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex flex-col"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={p.images?.[0]} alt={p.title} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3">
          <VerificationBadge status={p.verification_status} />
        </div>
        {p.status !== "available" && (
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide bg-black/70 text-white uppercase">
            {p.status.replace("_", " ")}
          </div>
        )}
        <div className="absolute bottom-3 left-3 font-mono-loop text-[10px] text-white/80 bg-black/50 px-2 py-0.5 rounded">
          {p.loop_id}
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-base font-bold text-white leading-snug line-clamp-2">{p.title}</h3>
        <p className="mt-1.5 flex items-center gap-1 text-xs text-slate-400">
          <MapPin className="w-3.5 h-3.5 text-[#C5A059]" /> {p.locality}, {p.city}
        </p>

        <div className="mt-4 flex items-end justify-between">
          <span className="text-xl font-extrabold text-white tracking-tight">{priceLabel(p)}</span>
          {p.price_per_sqft && (
            <span className="text-[11px] font-medium text-[#C5A059] bg-[#C5A059]/10 px-2 py-0.5 rounded border border-[#C5A059]/20">
              {inr(p.price_per_sqft)}/sqft
            </span>
          )}
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 py-3 border-y border-white/5">
          {facts.map((f, i) => (
            <div key={i} className="flex flex-col items-center gap-1 text-center">
              <f.Icon className="w-4 h-4 text-slate-500" />
              <span className="text-[11px] text-slate-300">{f.v}</span>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
          <span className="capitalize">{p.listed_by} listed</span>
          <span>Verified: {fmtDate(p.last_verified)}</span>
        </div>

        <Button data-testid={`view-property-${p.loop_id}`} onClick={() => nav(`/property/${p.id}`)}
          className="mt-4 w-full bg-transparent border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#070C1E] transition-colors group/btn">
          View Property <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
        </Button>
      </div>
    </motion.div>
  );
}
